import { DEFAULT_DIAL_CODE } from '@rentguru/commons-utils';
import { useField } from 'formik';
import isNil from 'lodash/isNil';
import { useEffect, useRef, useState } from 'react';
import { type Country, getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';

import { PhoneInput, type PhoneInputProps } from '@ui/components/fields/PhoneInput';

const toDialCode = (country: Country): string => `+${getCountryCallingCode(country)}`;

const DIAL_CODES_LONGEST_FIRST: string[] = Array.from(new Set(getCountries().map(toDialCode))).sort(
  (left, right) => right.length - left.length
);

const findCountryByDialCode = (dialCode: string | null | undefined): Country | undefined =>
  isNil(dialCode) ? undefined : getCountries().find((country) => toDialCode(country) === dialCode);

interface SplitPhoneValue {
  dialCode: string;
  number: string;
}

const splitPhoneValue = (value: string, currentDialCode: string): SplitPhoneValue => {
  const parsed = value === '' ? undefined : parsePhoneNumber(value);
  if (!isNil(parsed)) {
    return { dialCode: `+${parsed.countryCallingCode}`, number: parsed.nationalNumber.toString() };
  }
  const matchedDialCode = DIAL_CODES_LONGEST_FIRST.find((dialCode) => value.startsWith(dialCode));
  if (isNil(matchedDialCode)) {
    return { dialCode: currentDialCode, number: '' };
  }
  return { dialCode: matchedDialCode, number: value.slice(matchedDialCode.length) };
};

const toPhoneValue = ({ dialCode, number }: SplitPhoneValue): string => (number === '' ? '' : `${dialCode}${number}`);

interface FormikPhoneInputProps
  extends Omit<PhoneInputProps, 'value' | 'onChange' | 'error' | 'invalid' | 'defaultCountry' | 'onCountryChange'> {
  dialCodeName: string;
  numberName: string;
}

const FormikPhoneInput = ({ dialCodeName, numberName, ...rest }: FormikPhoneInputProps) => {
  const [dialCodeField, dialCodeMeta, dialCodeHelpers] = useField<string | null>(dialCodeName);
  const [numberField, numberMeta, numberHelpers] = useField<string | null>(numberName);

  const dialCode = dialCodeField.value ?? DEFAULT_DIAL_CODE;
  const formikValue = toPhoneValue({ dialCode, number: numberField.value ?? '' });
  const [draft, setDraft] = useState<string>(formikValue);
  const lastWrittenValueRef = useRef<string>(formikValue);

  useEffect(() => {
    if (formikValue !== lastWrittenValueRef.current) {
      lastWrittenValueRef.current = formikValue;
      setDraft(formikValue);
    }
  }, [formikValue]);

  const hasError = Boolean((numberMeta.touched || dialCodeMeta.touched) && (numberMeta.error || dialCodeMeta.error));
  const errorMessage = numberMeta.error ?? dialCodeMeta.error;

  const onChange = (nextValue: string) => {
    setDraft(nextValue);
    const split = splitPhoneValue(nextValue, dialCode);
    lastWrittenValueRef.current = toPhoneValue(split);
    if (split.dialCode !== dialCodeField.value) {
      dialCodeHelpers.setValue(split.dialCode);
    }
    if (split.number !== (numberField.value ?? '')) {
      numberHelpers.setValue(split.number);
    }
    numberHelpers.setTouched(true, false);
  };

  const onCountryChange = (country: Country | undefined) => {
    if (isNil(country)) {
      return;
    }
    const nextDialCode = toDialCode(country);
    if (nextDialCode !== dialCodeField.value) {
      lastWrittenValueRef.current = toPhoneValue({ dialCode: nextDialCode, number: numberField.value ?? '' });
      dialCodeHelpers.setValue(nextDialCode);
    }
  };

  return (
    <PhoneInput
      {...rest}
      value={draft}
      defaultCountry={findCountryByDialCode(dialCode)}
      onChange={onChange}
      onCountryChange={onCountryChange}
      error={hasError ? errorMessage : undefined}
    />
  );
};

export { FormikPhoneInput };
export type { FormikPhoneInputProps };
